import { addDays, format, startOfISOWeek } from "date-fns";

/**
 * Extracts the local date portion of a Date as "yyyy-MM-dd".
 *
 * Unlike toISOString().split("T")[0] — which converts to UTC first and can
 * shift the date backward in positive-offset timezones — this uses the
 * local year/month/day so the string always matches what the user sees on
 * their calendar.
 */
export function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Formats a Date as if it were in UTC, using the given date-fns format string.
 *
 * date-fns format() always formats using local time. To format in UTC we
 * construct a new Date whose local fields equal the original date's UTC fields,
 * then pass that to format().
 *
 * Example: new Date("2026-01-01T23:00:00+01:00") is 22:00 UTC.
 * formatDateUTC(date, "yyyy-MM-dd HH:mm:ss") → "2026-01-01 22:00:00"
 */
export function formatDateUTC(date: Date, formatStr: string): string {
  const utcEquivalent = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
  return format(utcEquivalent, formatStr);
}

/**
 * Converts a Date to "yyyy-MM-dd HH:mm:ss" in UTC.
 * This is the format the backend expects for scheduled_date.
 */
export function toUTCDateTimeString(date: Date): string {
  return formatDateUTC(date, "yyyy-MM-dd HH:mm:ss");
}

/**
 * Parses a UTC datetime string from the backend into a local Date.
 *
 * The backend returns dates like "2026-01-01 22:00:00" in UTC but without
 * a timezone indicator. new Date() would treat this as local time, losing
 * the UTC offset. This function normalizes the string to ISO 8601 with a
 * Z suffix so JavaScript correctly interprets it as UTC.
 *
 * Example (CET, UTC+1):
 *   parseUTCDate("2026-01-01 22:00:00").getHours() → 23
 */
export function parseUTCDate(dateStr: string): Date {
  return new Date(dateStr.replace(" ", "T") + "Z");
}

/**
 * Combines a new date string (yyyy-MM-dd) with the time portion from an
 * existing scheduled_date string ("yyyy-MM-dd HH:mm:ss" or ISO format).
 *
 * This preserves the original time when moving a ticket to a different date
 * (drag-drop, cut-paste, context menu date picker).
 *
 * If existingScheduledDate is null/undefined or contains no time portion,
 * defaults to 00:00:00.
 *
 * Examples:
 *   combineDateWithTime("2026-03-15", "2026-03-10 02:00:00") → "2026-03-15 02:00:00"
 *   combineDateWithTime("2026-03-15", null)                  → "2026-03-15 00:00:00"
 *   combineDateWithTime("2026-03-15", "2026-03-15")          → "2026-03-15 00:00:00"
 */
export function combineDateWithTime(newDateStr: string, existingScheduledDate: string | null | undefined): string {
  if (!existingScheduledDate) {
    return `${newDateStr} 00:00:00`;
  }

  // Split on space or "T" to extract the time portion
  const parts = existingScheduledDate.split(/[ T]/);
  if (parts.length < 2 || !parts[1]) {
    return `${newDateStr} 00:00:00`;
  }

  return `${newDateStr} ${parts[1]}`;
}

/**
 * Formats an ISO week's date range from any date within that week.
 *
 * Same month:      "24 – 30 Mar"
 * Cross-month:     "31 Mar – 6 Apr"
 * Year boundary:   "30 Dec – 5 Jan"
 */
export function formatWeekRange(date: Date): string {
  const weekStart = startOfISOWeek(date);
  const weekEnd = addDays(weekStart, 6);
  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${format(weekStart, 'd')} – ${format(weekEnd, 'd MMM')}`;
  }
  return `${format(weekStart, 'd MMM')} – ${format(weekEnd, 'd MMM')}`;
}
